import { useCallback } from 'react';
import { QueryType } from '../../../../utils/apollo/graphqlSchema';
import { formatNumberWithComma } from '../../../../utils/formatText';
import { assertCondition, assertNotNil } from '../../../../utils/types';
import { useChartContext } from '../../ChartContext';
import {
  ChartNumberType,
  IRetentionStandardRow,
  RetentionStandardRowType,
  RetentionTableType,
} from '../../interfaces';
import RetentionCellTooltip from '../RetentionCellTooltip';
import { CustomPopup } from '../RetentionCellTooltip/RetentionCellTooltipUI';
import { churnCellStyle, retentionCellStyle } from '../utils';
import getCellStyle from '../utils/getCellStyle';
import * as RetentionTableUI from './RetentionTableUI';
import { CellAlignment } from './RetentionTableUI';
import { COLUMN_OFFSET_PER_TYPE } from './utils';
import useFilteredRetentionTable from '../utils/useFilteredRetentionTable';
import { UnreachableCaseError } from 'ts-essentials';
import { RowHeaderDisplay } from './RowHeaderDisplay';
import { getHeaderName } from '../../../../utils/redux/transformRetentionData';
import { GridCellRenderer } from 'react-virtualized';
import { middleRowCell, staircaseReturnCell, styleWithBorder } from './borders';
import {
  defaultNumberFormatOptions,
  globalFormat,
} from '../../../../utils/formatting';
import useRetentionTooltip from '../../hooks/tooltip/useRetentionTooltip';
import usePrepareCohort from '../../hooks/usePrepareCohort';
import { CHART_TYPES } from '../../constants/chartTypes';
import { useFloatingLayerContext } from '../../../FloatingManager';
import KubitTooltip from '../../../KubitTooltip';

const getStartCellProps = (row: IRetentionStandardRow) => {
  if (row.type === RetentionStandardRowType.Average) {
    return { startCount: row.start.startCount, live: false };
  } else if (row.type === RetentionStandardRowType.Cohort) {
    return { startCount: row.start.startCount, live: row.start.live };
  } else {
    throw new UnreachableCaseError(row);
  }
};

export default (analysisId: string) => {
  const {
    chartData,
    settings: { chartSettings },
    displayedSlices,
    metadata: { query },
  } = useChartContext();
  assertCondition(chartData.queryType === QueryType.Retention);
  assertCondition(query?.__typename === 'RetentionQuery');

  const { handleCreateCohortFromRetention } = usePrepareCohort({
    analysisId,
  });
  const {
    tooltipProps,
    isTooltipLocked,
    onMouseCellMove,
    onMouseCellLeave,
    onCellClick,
    clearLockedTooltipProps,
  } = useRetentionTooltip();
  const { isChurn, chartNumberType } = chartSettings;
  const table = useFilteredRetentionTable({
    chartSettings,
    chartData,
    onlyVisibleMeasures: false,
  });

  // retention over time has 1 fixed column
  // retention has 2 fixed columns
  const columnOffset = COLUMN_OFFSET_PER_TYPE[table.type];

  const headerRenderer = useCallback<GridCellRenderer>(
    ({ columnIndex, key, style }) => {
      if (columnIndex === 0) {
        return (
          <RetentionTableUI.ColumnHeaderCell
            key={key}
            style={styleWithBorder(style, middleRowCell(columnIndex))}
          />
        );
      }

      if (columnIndex === 1 && table.type === RetentionTableType.Standard) {
        return (
          <RetentionTableUI.ColumnHeaderCell
            key={key}
            style={styleWithBorder(style, middleRowCell(columnIndex))}
            title={'Total'}
          >
            {'Total'}
          </RetentionTableUI.ColumnHeaderCell>
        );
      }

      const headerIndex = columnIndex - columnOffset;
      const columnHeader = table.columnHeaders[headerIndex];
      assertNotNil(columnHeader);

      return (
        <RetentionTableUI.ColumnHeaderCell
          key={key}
          style={styleWithBorder(style, middleRowCell(columnIndex))}
        >
          <KubitTooltip
            content={getHeaderName(columnHeader)}
            placement="bottom-start"
          >
            {getHeaderName(columnHeader)}
          </KubitTooltip>
        </RetentionTableUI.ColumnHeaderCell>
      );
    },
    [table, columnOffset],
  );

  const zIndex = useFloatingLayerContext();

  const retentionCellRenderer = useCallback<GridCellRenderer>(
    ({ columnIndex, key, rowIndex, style }) => {
      const isBackgroundFilled = rowIndex % 2 !== 0;
      // cohort date column
      if (columnIndex === 0) {
        return (
          <RetentionTableUI.RowHeaderCell
            key={key}
            style={styleWithBorder(style, middleRowCell(columnIndex))}
            $isBackgroundFilled={isBackgroundFilled}
            $alignment={CellAlignment.Left}
          >
            <RetentionTableUI.Value>
              <RowHeaderDisplay
                table={table}
                rowIndex={rowIndex}
                displayedSlices={displayedSlices}
              />
            </RetentionTableUI.Value>
          </RetentionTableUI.RowHeaderCell>
        );
      }

      // subjects column
      if (columnIndex === 1 && table.type === RetentionTableType.Standard) {
        const row = table.rows[rowIndex];
        const { startCount, live } = getStartCellProps(row);

        return (
          <RetentionTableUI.RowHeaderCell
            key={key}
            style={styleWithBorder(style, middleRowCell(columnIndex))}
            $isBackgroundFilled={isBackgroundFilled}
          >
            <RetentionTableUI.Value>
              {globalFormat.formatNumber(
                startCount,
                defaultNumberFormatOptions.integer,
              )}
            </RetentionTableUI.Value>
            {live && (
              <RetentionTableUI.RetentionLiveIcon
                name={'colored-orange-circles'}
                width={8}
                height={8}
              />
            )}
          </RetentionTableUI.RowHeaderCell>
        );
      }

      const retentionRow = table.rows[rowIndex];
      const { returns, sliceKey } = retentionRow;
      const returnIndex = columnIndex - columnOffset;

      // if the column index is greater than the number of retentions
      // do not display anything
      if (returnIndex >= returns.length) {
        return null;
      }

      const returnCell = returns[returnIndex];
      const { retentionCount, retentionPercent, churnCount, churnPercent } =
        returnCell;
      const live = 'live' in returnCell ? returnCell.live : false;

      const { backgroundColor, fontColor, iconKey } = isChurn
        ? getCellStyle(churnPercent, churnCellStyle, table.churnRange)
        : getCellStyle(
            retentionPercent,
            retentionCellStyle,
            table.retentionRange,
          );

      const retentionOrChurnPercentage = isChurn
        ? churnPercent
        : retentionPercent;
      const retentionOrChurnCount = isChurn ? churnCount : retentionCount;

      const formattedRetentionOrChurnPercentage = globalFormat.formatNumber(
        retentionOrChurnPercentage ?? 0,
        defaultNumberFormatOptions.percent,
      );

      const formattedRetentionOrChurnCount = formatNumberWithComma(
        retentionOrChurnCount,
      );

      const displayValue =
        chartNumberType === ChartNumberType.Number
          ? formattedRetentionOrChurnCount
          : formattedRetentionOrChurnPercentage;

      return (
        <RetentionTableUI.DataValueCell
          key={key}
          style={styleWithBorder(
            style,
            staircaseReturnCell(table, rowIndex, returnIndex),
          )}
          $isBackgroundFilled={isBackgroundFilled}
        >
          <CustomPopup
            $zIndex={zIndex}
            content={
              tooltipProps && (
                <RetentionCellTooltip
                  analysisId={analysisId}
                  tooltipProps={tooltipProps}
                  isTooltipLocked={isTooltipLocked}
                  clearLockedTooltipProps={clearLockedTooltipProps}
                  cellBackgroundColor={backgroundColor}
                  handleCreateCohort={handleCreateCohortFromRetention}
                />
              )
            }
            position={'bottom center'}
            offset={[0, 0]}
            open={!!tooltipProps && tooltipProps.key === key}
            trigger={
              <RetentionTableUI.ColorCodedValue
                $backgroundColor={backgroundColor}
                $fontColor={fontColor}
                onMouseOver={() =>
                  onMouseCellMove({
                    key,
                    returnCell,
                    sliceKey,
                    isChurn,
                    returnIndex,
                    chartType: CHART_TYPES.RETENTION,
                  })
                }
                onMouseLeave={onMouseCellLeave}
                onClick={() =>
                  onCellClick({
                    key,
                    returnCell,
                    sliceKey,
                    isChurn,
                    returnIndex,
                    chartType: CHART_TYPES.RETENTION,
                  })
                }
              >
                <RetentionTableUI.Value>
                  {displayValue}
                  {live && (
                    <RetentionTableUI.RetentionLiveIcon
                      name={iconKey}
                      width={8}
                      height={8}
                    />
                  )}
                </RetentionTableUI.Value>
              </RetentionTableUI.ColorCodedValue>
            }
          />
        </RetentionTableUI.DataValueCell>
      );
    },
    [
      table,
      columnOffset,
      isChurn,
      chartNumberType,
      zIndex,
      tooltipProps,
      analysisId,
      isTooltipLocked,
      clearLockedTooltipProps,
      handleCreateCohortFromRetention,
      onMouseCellLeave,
      displayedSlices,
      onMouseCellMove,
      onCellClick,
    ],
  );

  const cellRenderer = useCallback<GridCellRenderer>(
    ({ rowIndex, ...rest }) => {
      if (rowIndex === 0) {
        return headerRenderer({ rowIndex, ...rest });
      }

      return retentionCellRenderer({
        rowIndex: rowIndex - 1,
        ...rest,
      });
    },
    [headerRenderer, retentionCellRenderer],
  );

  return {
    cellRenderer,
  };
};
